import type { Triangle, Vec3, Vec2, Layer, SliceResult, Polygon } from './types';
import { meshBounds } from './mesh';

interface Segment {
  a: Vec2;
  b: Vec2;
}

/**
 * Slice a triangle mesh into Z-layers using mesh-plane intersection.
 * Each layer is a list of closed 2D polygons (loops).
 *
 * PERFORMANCE: builds a per-layer bucket index in one O(N) pass so each layer
 * iterates only its own triangle set instead of scanning the full mesh.
 * For a 256k-triangle vase × 200 layers this drops the inner loop from 50M
 * triangle-z-checks to ~total_intersections (typically <1M), a 50–500× win.
 */
export function sliceMesh(
  tris: Triangle[],
  layerHeight: number,
  firstLayerHeight = layerHeight
): SliceResult {
  const bounds = meshBounds(tris);
  if (tris.length === 0) {
    return { layers: [], meshBounds: bounds };
  }
  const minZ = bounds.min[2];
  const maxZ = bounds.max[2];

  // Build the list of slice Z heights — first layer slightly thinner if requested
  const zHeights: number[] = [];
  let z = minZ + firstLayerHeight;
  zHeights.push(z);
  while (z + layerHeight <= maxZ + 1e-6) {
    z += layerHeight;
    zHeights.push(z);
  }
  const L = zHeights.length;

  // Pre-compute per-triangle Z range — typed array for tight cache.
  const triMinZ = new Float32Array(tris.length);
  const triMaxZ = new Float32Array(tris.length);
  for (let i = 0; i < tris.length; i++) {
    const t = tris[i];
    const a = t.a[2], b = t.b[2], c = t.c[2];
    triMinZ[i] = Math.min(a, b, c);
    triMaxZ[i] = Math.max(a, b, c);
  }

  // Bucket layout: for each layer, an Int32Array of triangle indices that
  // straddle that layer's Z plane. Two-pass build keeps memory exact (no
  // resizable arrays), and total work is O(N + total intersections).
  //
  // Pass 1: count per-layer hits to size the index buffers.
  // Pass 2: fill them.
  const counts = new Int32Array(L);
  const eps = 1e-9;
  for (let i = 0; i < tris.length; i++) {
    const lo = triMinZ[i] - eps;
    const hi = triMaxZ[i] + eps;
    let firstLayer = Math.ceil((lo - zHeights[0]) / layerHeight);
    if (firstLayer < 0) firstLayer = 0;
    let lastLayer = Math.floor((hi - zHeights[0]) / layerHeight);
    if (lastLayer >= L) lastLayer = L - 1;
    if (firstLayer > lastLayer) continue;
    // Tighten by actual zHeights array (handles firstLayerHeight asymmetry where
    // the first slice doesn't sit on a uniform layerHeight grid).
    while (firstLayer < L && zHeights[firstLayer] < lo) firstLayer++;
    while (lastLayer >= 0 && zHeights[lastLayer] > hi) lastLayer--;
    if (firstLayer > lastLayer) continue;
    for (let li = firstLayer; li <= lastLayer; li++) counts[li]++;
  }
  const buckets: Int32Array[] = new Array(L);
  const offsets = new Int32Array(L);
  for (let li = 0; li < L; li++) buckets[li] = new Int32Array(counts[li]);
  for (let i = 0; i < tris.length; i++) {
    const lo = triMinZ[i] - eps;
    const hi = triMaxZ[i] + eps;
    let firstLayer = Math.ceil((lo - zHeights[0]) / layerHeight);
    if (firstLayer < 0) firstLayer = 0;
    let lastLayer = Math.floor((hi - zHeights[0]) / layerHeight);
    if (lastLayer >= L) lastLayer = L - 1;
    if (firstLayer > lastLayer) continue;
    while (firstLayer < L && zHeights[firstLayer] < lo) firstLayer++;
    while (lastLayer >= 0 && zHeights[lastLayer] > hi) lastLayer--;
    if (firstLayer > lastLayer) continue;
    for (let li = firstLayer; li <= lastLayer; li++) {
      buckets[li][offsets[li]++] = i;
    }
  }

  const layers: Layer[] = new Array(L);
  for (let li = 0; li < L; li++) {
    const sliceZ = zHeights[li];
    const bucket = buckets[li];
    const segments: Segment[] = [];
    for (let bi = 0; bi < bucket.length; bi++) {
      const seg = trianglePlaneIntersect(tris[bucket[bi]], sliceZ);
      if (seg) segments.push(seg);
    }
    layers[li] = { z: sliceZ, loops: stitchSegments(segments) };
  }

  return { layers, meshBounds: bounds };
}

/** ===== Worker-pool helpers =====
 *  Splits the per-layer slicing work between multiple Web Workers. The bucket
 *  index is built ONCE on the main thread (cheap, single pass), then each
 *  worker receives the triangle subset its layers actually need plus the
 *  remapped buckets. Net effect: serialization cost ~1× full mesh per slice
 *  request, parallel compute = N× cores. */

export interface BucketIndex {
  zHeights: number[];
  /** Per-layer triangle indices into the full triangle list. */
  buckets: Int32Array[];
  bounds: ReturnType<typeof meshBounds>;
}

export function buildBucketIndex(
  tris: Triangle[],
  layerHeight: number,
  firstLayerHeight = layerHeight
): BucketIndex {
  const bounds = meshBounds(tris);
  if (tris.length === 0) return { zHeights: [], buckets: [], bounds };
  const minZ = bounds.min[2];
  const maxZ = bounds.max[2];
  const zHeights: number[] = [];
  let z = minZ + firstLayerHeight;
  zHeights.push(z);
  while (z + layerHeight <= maxZ + 1e-6) {
    z += layerHeight;
    zHeights.push(z);
  }
  const L = zHeights.length;
  const counts = new Int32Array(L);
  const eps = 1e-9;
  for (let i = 0; i < tris.length; i++) {
    const t = tris[i];
    const lo = Math.min(t.a[2], t.b[2], t.c[2]) - eps;
    const hi = Math.max(t.a[2], t.b[2], t.c[2]) + eps;
    let lo_i = Math.ceil((lo - zHeights[0]) / layerHeight);
    if (lo_i < 0) lo_i = 0;
    let hi_i = Math.floor((hi - zHeights[0]) / layerHeight);
    if (hi_i >= L) hi_i = L - 1;
    while (lo_i < L && zHeights[lo_i] < lo) lo_i++;
    while (hi_i >= 0 && zHeights[hi_i] > hi) hi_i--;
    if (lo_i > hi_i) continue;
    for (let li = lo_i; li <= hi_i; li++) counts[li]++;
  }
  const buckets: Int32Array[] = new Array(L);
  const offsets = new Int32Array(L);
  for (let li = 0; li < L; li++) buckets[li] = new Int32Array(counts[li]);
  for (let i = 0; i < tris.length; i++) {
    const t = tris[i];
    const lo = Math.min(t.a[2], t.b[2], t.c[2]) - eps;
    const hi = Math.max(t.a[2], t.b[2], t.c[2]) + eps;
    let lo_i = Math.ceil((lo - zHeights[0]) / layerHeight);
    if (lo_i < 0) lo_i = 0;
    let hi_i = Math.floor((hi - zHeights[0]) / layerHeight);
    if (hi_i >= L) hi_i = L - 1;
    while (lo_i < L && zHeights[lo_i] < lo) lo_i++;
    while (hi_i >= 0 && zHeights[hi_i] > hi) hi_i--;
    if (lo_i > hi_i) continue;
    for (let li = lo_i; li <= hi_i; li++) {
      buckets[li][offsets[li]++] = i;
    }
  }
  return { zHeights, buckets, bounds };
}

/** Slice an explicit layer index range using a pre-built bucket index. Used by
 *  the worker pool — each worker handles [layerStart, layerEnd) in parallel. */
export function sliceLayerRange(
  tris: Triangle[],
  index: BucketIndex,
  layerStart: number,
  layerEnd: number
): Layer[] {
  const out: Layer[] = new Array(layerEnd - layerStart);
  for (let li = layerStart; li < layerEnd; li++) {
    const sliceZ = index.zHeights[li];
    const bucket = index.buckets[li];
    const segments: Segment[] = [];
    for (let bi = 0; bi < bucket.length; bi++) {
      const seg = trianglePlaneIntersect(tris[bucket[bi]], sliceZ);
      if (seg) segments.push(seg);
    }
    out[li - layerStart] = { z: sliceZ, loops: stitchSegments(segments) };
  }
  return out;
}

/**
 * Intersect a triangle with a horizontal plane Z = z. Returns the 2D segment of intersection,
 * or null if the triangle is degenerate / coplanar / on one side.
 */
function trianglePlaneIntersect(t: Triangle, z: number): Segment | null {
  const verts: Vec3[] = [t.a, t.b, t.c];
  const above: number[] = [];
  const below: number[] = [];
  const on: number[] = [];

  for (let i = 0; i < 3; i++) {
    const dz = verts[i][2] - z;
    if (Math.abs(dz) < 1e-9) on.push(i);
    else if (dz > 0) above.push(i);
    else below.push(i);
  }

  // Coplanar triangle — skip; would create a sliver, handled by adjacent triangles.
  if (on.length === 3) return null;

  // All on one side — no intersection.
  if (above.length === 3 || below.length === 3) return null;

  // Two vertices on the plane → one edge IS the segment.
  if (on.length === 2) {
    const v1 = verts[on[0]];
    const v2 = verts[on[1]];
    return { a: [v1[0], v1[1]], b: [v2[0], v2[1]] };
  }

  // One vertex on the plane → segment from that vertex to the intersection of the opposite edge.
  if (on.length === 1) {
    const onIdx = on[0];
    const otherA = (onIdx + 1) % 3;
    const otherB = (onIdx + 2) % 3;
    if ((above.includes(otherA) && above.includes(otherB)) ||
        (below.includes(otherA) && below.includes(otherB))) {
      // Both opposite vertices on same side — only the on-plane vertex touches; skip (point, not segment).
      return null;
    }
    const ip = edgePlaneIntersect(verts[otherA], verts[otherB], z);
    if (!ip) return null;
    return { a: [verts[onIdx][0], verts[onIdx][1]], b: ip };
  }

  // No vertices on the plane → two edges are crossed by the plane.
  const minoritySide = above.length === 1 ? above : below;
  const isolated = minoritySide[0];
  const others = [0, 1, 2].filter((i) => i !== isolated);
  const i1 = edgePlaneIntersect(verts[isolated], verts[others[0]], z);
  const i2 = edgePlaneIntersect(verts[isolated], verts[others[1]], z);
  if (!i1 || !i2) return null;
  return { a: i1, b: i2 };
}

function edgePlaneIntersect(p1: Vec3, p2: Vec3, z: number): Vec2 | null {
  const dz = p2[2] - p1[2];
  if (Math.abs(dz) < 1e-12) return null;
  const t = (z - p1[2]) / dz;
  if (t < -1e-6 || t > 1 + 1e-6) return null;
  return [p1[0] + (p2[0] - p1[0]) * t, p1[1] + (p2[1] - p1[1]) * t];
}

/**
 * Stitch unordered 2D segments into closed polygons by matching endpoints.
 * Uses a quantized hash so floating-point endpoints from different triangles still match.
 */
function stitchSegments(segments: Segment[]): Polygon[] {
  if (segments.length === 0) return [];
  const QUANT = 1e4; // 0.1 µm precision
  const key = (p: Vec2): string => `${Math.round(p[0] * QUANT)}|${Math.round(p[1] * QUANT)}`;

  // Build adjacency: for each endpoint key, list of segments touching it
  const map = new Map<string, number[]>();
  segments.forEach((s, i) => {
    const ka = key(s.a);
    const kb = key(s.b);
    if (!map.has(ka)) map.set(ka, []);
    if (!map.has(kb)) map.set(kb, []);
    map.get(ka)!.push(i);
    map.get(kb)!.push(i);
  });

  const used = new Uint8Array(segments.length);
  const loops: Polygon[] = [];

  for (let start = 0; start < segments.length; start++) {
    if (used[start]) continue;
    used[start] = 1;
    const loop: Vec2[] = [segments[start].a, segments[start].b];
    let currentKey = key(segments[start].b);
    let safety = segments.length + 4;

    while (safety-- > 0) {
      const candidates = map.get(currentKey);
      if (!candidates) break;
      let nextIdx = -1;
      for (const ci of candidates) {
        if (!used[ci]) {
          nextIdx = ci;
          break;
        }
      }
      if (nextIdx === -1) break;
      used[nextIdx] = 1;
      const seg = segments[nextIdx];
      const segAKey = key(seg.a);
      const nextPt = segAKey === currentKey ? seg.b : seg.a;
      const startKey = key(loop[0]);
      const nextPtKey = key(nextPt);
      if (nextPtKey === startKey) {
        // Closed loop
        loops.push(loop);
        break;
      }
      loop.push(nextPt);
      currentKey = nextPtKey;
    }
    // Open loops are dropped — they indicate a non-manifold mesh; we could optionally
    // emit them as polylines but for slicing we want closed contours.
  }
  return loops;
}
